import React, { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Button } from "reactstrap";
import { FaStar } from "react-icons/fa";
import { getSpotlight, addSpotlight, deleteSpotlight, uploadImage } from "./UpdatesApi";
import EditModal from "../../Components/Shared/EditModal";

const ACCENT = "#534AB7";

const getInitials = (name = "") =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

// const API_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") || "http://www.sswhite.systems:7168";

// const getImageSrc = (url) => {
//     if (!url) return null;
//     // Extract just the /uploads/... part
//     const match = url.match(/\/uploads\/.+/);
//     if (!match) return url;
//     // Prepend the API base URL
//     return `${API_BASE}${match[0]}`;
// };

// Round HOST-1: ?? (not ||) — in production the env var is DEFINED AS
// EMPTY on purpose, meaning "same origin" (host-relative /uploads/...).
// Only a build with the var entirely ABSENT (local dev) falls back to the
// localhost API host, exactly as before.
const SERVER_BASE = process.env.REACT_APP_SERVER_BASE ?? "https://localhost:7168";

const getImageSrc = (url) => {
    if (!url) return null;
    const match = url.match(/\/uploads\/.+/);
    if (!match) return url;
    return `${SERVER_BASE}${match[0]}`;
};

const SpotlightCard = ({ role }) => {
    const canEdit = ["Manager", "HR Admin"].includes(role);

    const [items, setItems] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [content, setContent] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [employeeName, setEmployeeName] = useState("");
    const [employeeRole, setEmployeeRole] = useState("");

    const load = async () => {
        const r = await getSpotlight();
        setItems(r);
    };

    useEffect(() => { load(); }, []);

    const onSave = async () => {
        let uploadedUrl = null;
        if (imageFile) uploadedUrl = await uploadImage(imageFile);

        const payload = {
            id: 0, type: "Spotlight", title: "Employee Spotlight",
            description: content, date: new Date().toISOString(),
            imageUrl: uploadedUrl, employeeImageUrl: uploadedUrl,
            employeeName, employeeRole, eventLocation: "", eventTime: ""
        };

        await addSpotlight(payload);
        setEmployeeName(""); setEmployeeRole(""); setContent("");
        setImageFile(null); setImagePreview(""); setModalOpen(false);
        load();
    };

    const remove = async (id) => {
        await deleteSpotlight(id);
        load();
    };

    return (
        <Card style={styles.card}>
            <CardBody style={styles.cardBody}>
                <div style={{ ...styles.accentBar, background: ACCENT }} />

                {/* Header */}
                <div style={styles.header}>
                    <CardTitle tag="h6" style={styles.title}>
                        <div style={styles.iconWrap}>
                            <FaStar size={15} color={ACCENT} />
                        </div>
                        Employee Spotlight
                    </CardTitle>
                    {canEdit && (
                        <Button size="sm" onClick={() => setModalOpen(true)} style={styles.addBtn}>
                            + Add
                        </Button>
                    )}
                </div>

                {/* List */}
                <ul style={styles.list}>
                    {items.length ? items.map((i, idx) => (
                        <li key={i.id} style={{ ...styles.item, borderBottom: idx < items.length - 1 ? "0.5px solid #f0f0f0" : "none" }}>
                            {/* Avatar — image or initials fallback */}
                            {i.imageUrl ? (
                                <img
                                    src={getImageSrc(i.imageUrl)}  // ✅ fixed
                                    alt={i.employeeName}
                                    style={styles.avatar}
                                />
                            ) : (
                                <div style={styles.initialsAvatar}>
                                    {getInitials(i.employeeName)}
                                </div>
                            )}

                            {/* Info */}
                            <div style={styles.info}>
                                {idx === 0 && (
                                    <span style={styles.featuredBadge}>Featured this month</span>
                                )}
                                <div style={styles.empName}>{i.employeeName}</div>
                                <div style={styles.empRole}>{i.employeeRole}</div>
                                <div style={styles.empDesc}>{i.description}</div>
                                {canEdit && (
                                    <Button color="danger" outline size="sm" style={styles.deleteBtn}
                                        onClick={() => remove(i.id)}>
                                        Delete
                                    </Button>
                                )}
                            </div>
                        </li>
                    )) : (
                        <p style={styles.empty}>No employee spotlight items yet.</p>
                    )}
                </ul>

                <EditModal
                    isOpen={modalOpen}
                    toggle={(action) => { if (action === "save") onSave(); else setModalOpen(false); }}
                    title="Add Spotlight"
                    content={content} setContent={setContent}
                    imageFile={imageFile}
                    setImageFile={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }}
                    imagePreview={imagePreview}
                    employeeName={employeeName} setEmployeeName={setEmployeeName}
                    employeeRole={employeeRole} setEmployeeRole={setEmployeeRole}
                />
            </CardBody>
        </Card>
    );
};

const styles = {
    card: { borderRadius: "16px", border: "0.5px solid #e8e8e8", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden", position: "relative", height: "100%" },
    cardBody: { padding: "20px 20px 14px" },
    accentBar: { position: "absolute", top: 0, left: 0, width: "4px", height: "100%", borderRadius: "16px 0 0 16px" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" },
    title: { margin: 0, fontWeight: 600, fontSize: "14px", color: "#2C2C2A", display: "flex", alignItems: "center", gap: "8px" },
    iconWrap: { width: "30px", height: "30px", borderRadius: "8px", background: "#EEEDFE", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    addBtn: { background: "#534AB7", border: "none", borderRadius: "8px", fontSize: "12px", padding: "4px 12px", color: "#fff" },
    list: { listStyle: "none", padding: 0, margin: 0 },
    item: { display: "flex", alignItems: "center", gap: "14px", padding: "12px 0" },
    avatar: { width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #EEEDFE" },
    initialsAvatar: { width: "52px", height: "52px", borderRadius: "50%", background: "#EEEDFE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: 600, color: "#534AB7", flexShrink: 0 },
    info: { display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-start" },
    featuredBadge: { fontSize: "10px", background: "#EEEDFE", color: "#534AB7", borderRadius: "20px", padding: "2px 10px", fontWeight: 500, marginBottom: "2px" },
    empName: { fontSize: "13px", fontWeight: 600, color: "#2C2C2A" },
    empRole: { fontSize: "12px", color: "#888780" },
    empDesc: { fontSize: "12px", color: "#888780" },
    deleteBtn: { fontSize: "11px", padding: "2px 8px", marginTop: "4px" },
    empty: { fontSize: "13px", color: "#888780", margin: 0 },
};

export default SpotlightCard;